import { asArray, toFiniteFirst } from '@cornerstonejs/utils';
import { compareFinite, hasNoValue, looseEquals } from './values.js';
import { invalidAt, matchForm, matchTestShape } from './schema.js';
const isScalar = (value) => typeof value === 'string' ||
    typeof value === 'boolean' ||
    (typeof value === 'number' && Number.isFinite(value));
const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const isScalarOrScalars = (value) => isScalar(value) ||
    (Array.isArray(value) && value.length > 0 && value.every(isScalar));
const isTextOrTexts = (value) => typeof value === 'string' ||
    (Array.isArray(value) &&
        value.length > 0 &&
        value.every((item) => typeof item === 'string'));
const elementsOf = (value) => hasNoValue(value) ? [] : asArray(value).filter((item) => !hasNoValue(item));
const textsOf = (value, ignoreCase = false) => elementsOf(value).map((item) => ignoreCase ? String(item).toLowerCase() : String(item));
const needlesOf = (operand, ignoreCase = false) => asArray(operand).map((item) => ignoreCase ? String(item).toLowerCase() : String(item));
function equalsTest(operand) {
    return operand === null
        ? (value) => hasNoValue(value)
        : (value) => looseEquals(value, operand);
}
function includesTest(operand) {
    const wanted = asArray(operand);
    return (value) => elementsOf(value).some((item) => wanted.some((element) => looseEquals(item, element)));
}
function textTest(ignoreCase, test) {
    return (operand) => {
        const needles = needlesOf(operand, ignoreCase);
        return (value) => textsOf(value, ignoreCase).some((text) => needles.some((needle) => test(text, needle)));
    };
}
const not = (compile) => (operand) => {
    const test = compile(operand);
    return (value, subject, context) => !test(value, subject, context);
};
function numberTest(test) {
    const compare = compareFinite(test);
    return (operand) => (value) => compare(value, operand);
}
const contains = textTest(false, (text, needle) => text.includes(needle));
const containsI = textTest(true, (text, needle) => text.includes(needle));
const startsWith = textTest(false, (text, needle) => text.startsWith(needle));
const endsWith = textTest(false, (text, needle) => text.endsWith(needle));
export const MATCH_HELPERS = Object.freeze({
    includes: (value, operand) => includesTest(operand)(value),
    contains: (value, operand) => contains(operand)(value),
    startsWith: (value, operand) => startsWith(operand)(value),
    endsWith: (value, operand) => endsWith(operand)(value),
});
export const MATCH_OPERATORS = Object.freeze({
    equals: {
        operand: 'a scalar, or null',
        accepts: (operand) => operand === null || isScalar(operand),
        compile: equalsTest,
    },
    doesNotEqual: {
        operand: 'a scalar, or null',
        accepts: (operand) => operand === null || isScalar(operand),
        compile: not(equalsTest),
    },
    notNull: {
        operand: 'a boolean',
        accepts: (operand) => typeof operand === 'boolean',
        compile: (operand) => operand ? (value) => !hasNoValue(value) : (value) => hasNoValue(value),
    },
    includes: {
        operand: 'a scalar, or a non-empty array of scalars',
        accepts: isScalarOrScalars,
        compile: includesTest,
    },
    doesNotInclude: {
        operand: 'a scalar, or a non-empty array of scalars',
        accepts: isScalarOrScalars,
        compile: not(includesTest),
    },
    greaterThan: {
        operand: 'a finite number',
        accepts: isFiniteNumber,
        compile: numberTest((value, operand) => value > operand),
    },
    lessThan: {
        operand: 'a finite number',
        accepts: isFiniteNumber,
        compile: numberTest((value, operand) => value < operand),
    },
    range: {
        operand: 'an array of two finite numbers',
        accepts: (operand) => Array.isArray(operand) &&
            operand.length === 2 &&
            operand.every(isFiniteNumber),
        compile: (operand) => {
            const [a, b] = operand;
            const low = Math.min(a, b);
            const high = Math.max(a, b);
            return (value) => {
                const number = toFiniteFirst(value);
                return number !== undefined && number >= low && number <= high;
            };
        },
    },
    contains: {
        operand: 'a string, or a non-empty array of strings',
        accepts: isTextOrTexts,
        compile: contains,
    },
    containsI: {
        operand: 'a string, or a non-empty array of strings',
        accepts: isTextOrTexts,
        compile: containsI,
    },
    doesNotContain: {
        operand: 'a string, or a non-empty array of strings',
        accepts: isTextOrTexts,
        compile: not(contains),
    },
    doesNotContainI: {
        operand: 'a string, or a non-empty array of strings',
        accepts: isTextOrTexts,
        compile: not(containsI),
    },
    containsAll: {
        operand: 'a scalar, or a non-empty array of scalars',
        accepts: isScalarOrScalars,
        compile: (operand) => {
            const wanted = asArray(operand);
            return (value) => {
                const elements = elementsOf(value);
                return (elements.length > 0 &&
                    wanted.every((element) => elements.some((item) => looseEquals(item, element))));
            };
        },
    },
    startsWith: {
        operand: 'a string, or a non-empty array of strings',
        accepts: isTextOrTexts,
        compile: startsWith,
    },
    endsWith: {
        operand: 'a string, or a non-empty array of strings',
        accepts: isTextOrTexts,
        compile: endsWith,
    },
});
export function compileMatchTest(test, site) {
    if (typeof test === 'function') {
        return test;
    }
    if (test === null || isScalar(test)) {
        return equalsTest(test);
    }
    if (Array.isArray(test)) {
        if (!test.length || !test.every(isScalar)) {
            return invalidAt(site, 'an array test must hold one or more scalars', test);
        }
        return includesTest(test);
    }
    if (!test || typeof test !== 'object') {
        return invalidAt(site, 'expected a scalar, an array of scalars, null, an object with one operator, or a function', test);
    }
    matchForm(matchTestShape, test, site);
    const [name, operand] = Object.entries(test)[0];
    const operator = MATCH_OPERATORS[name];
    if (!operator.accepts(operand)) {
        return invalidAt(site, `the operand of ${name} must be ${operator.operand}`, test);
    }
    return operator.compile(operand);
}
